import { v4 as uuidv4 } from 'uuid';
import {
  LANE_COOLDOWN_MS,
  MIN_LANES,
  NAME_CHIP_MAX_WIDTH,
  NEIGHBOR_STAGGER_MS,
  NEIGHBOR_STAGGER_WINDOW_MS,
  REACTIONS_ZONE_LEFT_INSET,
} from 'features/room-reactions/constants';
import { Reaction, ReactionItem } from 'features/room-reactions/types';
import { UserName } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { LaneAllocator } from 'features/room-reactions/utils/LaneAllocator';
import { LaneDrainEntry, LaneDrainQueue } from 'features/room-reactions/utils/LaneDrainQueue';
import {
  computeChipMaxWidth,
  computeLaneCount,
  computeLeftForLane,
  computeZoneLeft,
  computeZoneWidth,
} from 'features/room-reactions/utils/reactionsLayout';
import { ReactionsManager } from 'features/room-reactions/utils/ReactionsManager';
import { store } from 'store/store';

export type FormattedReactionItem = {
  id: string;
  reaction: Reaction;
  senderName: UserName;
  local: boolean;
  left: number;
  chipMaxWidth: number;
  delayMs: number;
};

type Layout = {
  zoneWidth: number;
  zoneLeft: number;
  laneCount: number;
  chipMaxWidth: number;
};

type ItemsListener = (items: readonly FormattedReactionItem[]) => void;

const INITIAL_LAYOUT: Layout = {
  zoneWidth: 0,
  zoneLeft: REACTIONS_ZONE_LEFT_INSET,
  laneCount: MIN_LANES,
  chipMaxWidth: NAME_CHIP_MAX_WIDTH,
};

export class ReactionsDisplayManager {
  private readonly allocator = new LaneAllocator({
    cooldownMs: LANE_COOLDOWN_MS,
    neighborStaggerMs: NEIGHBOR_STAGGER_MS,
    neighborStaggerWindowMs: NEIGHBOR_STAGGER_WINDOW_MS,
  });

  private readonly queue = new LaneDrainQueue<ReactionItem>(this.allocator, (batch) =>
    this.appendBatch(batch)
  );

  private items: readonly FormattedReactionItem[] = [];

  private listeners: Set<ItemsListener> = new Set();

  private layout: Layout = INITIAL_LAYOUT;

  private unsubscribeIncoming: (() => void) | null = null;

  subscribe(listener: ItemsListener): () => void {
    this.listeners.add(listener);
    if (this.listeners.size === 1) {
      this.activate();
    }
    listener(this.items);

    return () => {
      if (!this.listeners.delete(listener)) {
        return;
      }
      if (this.listeners.size === 0) {
        this.deactivate();
      }
    };
  }

  setContainerWidth(width: number): void {
    const chipMaxWidth = computeChipMaxWidth(width);
    const zoneWidth = computeZoneWidth(width, chipMaxWidth);
    const zoneLeft = computeZoneLeft(chipMaxWidth);
    const laneCount = computeLaneCount(zoneWidth);

    this.layout = { chipMaxWidth, zoneWidth, zoneLeft, laneCount };
    this.allocator.setLaneCount(laneCount);
  }

  removeItem(id: string): void {
    const next = this.items.filter((item) => item.id !== id);
    if (next.length === this.items.length) {
      return;
    }
    this.items = next;
    this.emit();
  }

  private activate(): void {
    this.unsubscribeIncoming = ReactionsManager.addListener(this.handleIncoming);
    document.addEventListener('visibilitychange', this.handlePageHidden);
  }

  private deactivate(): void {
    this.unsubscribeIncoming?.();
    this.unsubscribeIncoming = null;
    document.removeEventListener('visibilitychange', this.handlePageHidden);
    this.resetItems();
  }

  private handleIncoming = (reactions: ReactionItem[]): void => {
    if (document.hidden) {
      return;
    }
    this.queue.push(reactions);
  };

  private handlePageHidden = (): void => {
    if (!document.hidden) {
      return;
    }
    this.resetItems();
  };

  private appendBatch(batch: ReadonlyArray<LaneDrainEntry<ReactionItem>>): void {
    this.items = [...this.items, ...batch.map((entry) => this.formatReaction(entry))];
    this.emit();
  }

  private formatReaction({
    item,
    lane,
    delayMs,
  }: LaneDrainEntry<ReactionItem>): FormattedReactionItem {
    const { zoneLeft, zoneWidth, laneCount, chipMaxWidth } = this.layout;

    const localUserId = selectLocalUserId(store.getState());

    return {
      id: uuidv4(),
      reaction: item.reaction,
      senderName: item.initiator.name,
      local: item.initiator.id === localUserId,
      left: computeLeftForLane(lane, zoneLeft, zoneWidth, laneCount),
      chipMaxWidth,
      delayMs,
    };
  }

  private resetItems(): void {
    this.queue.clear();
    if (this.items.length === 0) {
      return;
    }
    this.items = [];
    this.emit();
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener(this.items));
  }
}
